import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import apiClient from '../utils/api';
import { toast } from 'sonner';

const VOICES = [
  { id: 'minimalist',   name: 'Minimalist',   blurb: 'Clean, short, no fluff' },
  { id: 'luxury',       name: 'Luxury',       blurb: 'Sophisticated, aspirational' },
  { id: 'bold',         name: 'Bold',         blurb: 'Punchy, high energy' },
  { id: 'witty',        name: 'Witty',        blurb: 'Playful, clever' },
  { id: 'professional', name: 'Professional', blurb: 'Factual, broker-grade' },
];

/**
 * Small inline "Rewrite as…" dropdown for any text field. Sends the current
 * text + a chosen brand voice to `/api/social/text/rewrite` and replaces the
 * text on success. Designed for the Newsletter description block where the
 * scraped property summary is usually verbose.
 */
export const RewriteWithAI = ({ currentText, onRewritten, disabled = false, maxWords = 120 }) => {
  const [busy, setBusy] = useState(false);

  const rewrite = async (voiceId) => {
    const text = (currentText || '').trim();
    if (!text) {
      toast.error('Nothing to rewrite — add some text first');
      return;
    }
    setBusy(true);
    try {
      const resp = await apiClient.post('/social/text/rewrite', {
        text,
        brand_voice: voiceId,
        max_words: maxWords,
      });
      const rewritten = resp?.data?.rewritten;
      if (rewritten) {
        onRewritten(rewritten);
        toast.success(`Rewritten in ${voiceId} voice`);
      } else {
        toast.error('Empty response from AI — try again');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Rewrite failed';
      toast.error(typeof msg === 'string' ? msg : 'Rewrite failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" variant="ghost" disabled={disabled || busy} className="h-7 px-2 gap-1 text-xs" data-testid="newsletter-rewrite-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Rewrite
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Rewrite as…</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {VOICES.map((v) => (
          <DropdownMenuItem
            key={v.id}
            onClick={() => rewrite(v.id)}
            data-testid={`newsletter-rewrite-${v.id}`}
            className="flex flex-col items-start gap-0 cursor-pointer"
          >
            <span className="text-sm font-medium">{v.name}</span>
            <span className="text-[11px] text-muted-foreground">{v.blurb}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RewriteWithAI;
